import { env } from "@/env";

/**
 * Centralized application configuration
 * All environment variables are read from here
 */

// Global flags
const appName = env.VITE_APP_NAME || "webapp";
const isChinaApp = appName === "webapp-hk";

// SmartCat translation is auto-enabled for China app
const isSmartCatEnabled = isChinaApp || env.VITE_IS_SMARTCAT_ENABLED === "true";

export const config = {
	// Global settings
	global: {
		env: import.meta.env.MODE || "development",
		appName,
		isChinaApp,
		isSmartCatEnabled,
	},

	// URLs
	urls: {
		baseServiceUrl: env.VITE_API_BASE_URL,
		baseServiceUrlChina: env.VITE_BASE_SERVICE_URL_CHINA,
		webServiceUrl: env.VITE_WEB_SERVICE_URL || "https://amberstudent.com",
		webServiceHostname: env.VITE_WEB_SERVICE_HOST || "amberstudent.com",
		radarUrl: env.VITE_RADAR_URL || "https://radar.amberstudent.com",
	},

	// Auth
	auth: {
		loginUrl: env.VITE_LOGIN_URL,
		googleClientId: env.VITE_GOOGLE_CLIENT_ID,
		facebookAppId: env.VITE_FACEBOOK_APP_ID,
		appleClientId: env.VITE_APPLE_CLIENT_ID,
	},
	// Analytics
	analytics: {
		gaMeasurementId: env.VITE_GA_MEASUREMENT_ID,
		// Google Analytics disabled for China (Google blocked)
		isEnabled: !isChinaApp,
	},

	// PostHog
	posthog: {
		apiKey: env.VITE_POSTHOG_API_KEY || "",
		host: env.VITE_POSTHOG_HOST || "https://posthog.amberstudent.com",
		isEnabled: !!env.VITE_POSTHOG_API_KEY,
	},

	// Metabase
	metabase: {
		apiKey: env.VITE_METABASE_API_KEY,
		dashboardUrl: "https://amberhq.metabaseapp.com/embed/dashboard",
		dashboardId: 364,
	},

	// Zendesk
	zendesk: {
		isVisible: env.VITE_ZENDESK_IS_VISIBLE === "true",
		url: env.VITE_ZENDESK_URL,
	},

	// Intercom (fallback for Zendesk)
	intercom: {
		appId: env.VITE_INTERCOM_APP_ID || "lw5f27r8",
	},

	// Sentry
	sentry: {
		dsn: env.VITE_SENTRY_DSN,
		environment: env.VITE_DEPLOYMENT_ENVIRONMENT,
	},

	// Firebase - disabled for China (Google blocked)
	firebase: {
		projectId: env.VITE_FIREBASE_PROJECT_ID,
		isEnabled: !isChinaApp,
	},

	// Stripe
	stripe: {
		apiKey: env.VITE_STRIPE_API_KEY,
	},

	// Weglot
	weglot: {
		apiKey: env.VITE_WEGLOT_API_KEY,
	},

	// Image Assets
	assets: {
		imageUrl:
			env.VITE_IMAGE_ASSETS_URL ||
			"https://prod-static-assets.amberstudent.com",
		staticImageUrl:
			env.VITE_IMAGE_STATIC_ASSETS_URL ||
			"https://prod-static-assets.amberstudent.com",
		/** S3 base URL for inventory/partner images */
		s3BaseUrl: "https://assets.amberstudent.com",
		/** China OSS CDN for flag icons */
		chinaOssUrl: "https://oss-assets.amberstudent.cn/assets/static",
		/** China CDN for template icons (tab bar, etc.) */
		chinaTemplateUrl:
			"https://static-assets-ali.amberstudent.cn/assets/templates",
	},

	// reCAPTCHA - disabled for China (Google blocked)
	recaptcha: {
		siteKey: env.VITE_RECAPTCHA_SITE_KEY,
		isEnabled: !isChinaApp,
	},

	// Freshdesk
	freshdesk: {
		apiKey: env.VITE_FRESHDESK_API_KEY,
	},

	// CometChat
	cometChat: {
		authKey: env.VITE_COMET_CHAT_AUTH_KEY,
	},

	// Phone numbers (China vs Global)
	phone: isChinaApp
		? {
				countryCode: "+86",
				countryCodeRaw: "86",
				countryCodeName: "cn",
				fullNumber: "+86 13839566793",
				number: "13839566793",
			}
		: {
				countryCode: "+44",
				countryCodeRaw: "44",
				countryCodeName: "gb",
				fullNumber: "+44 7723497306",
				number: "7723497306",
			},

	// Logo URLs (China vs Global)
	logo: {
		url: isChinaApp
			? "https://s3.ap-southeast-1.amazonaws.com/static-assets.amberstudent.com/images/china_logo_amber.svg?auto=format"
			: "https://prod-static-assets.amberstudent.com/images/amber-hd.svg?auto=format",
	},

	// Social login (China uses WeChat, Global uses Google/Facebook)
	social: {
		useWeChat: isChinaApp,
		useGoogle: !isChinaApp,
		useFacebook: !isChinaApp,
		useApple: true, // Apple available for both
	},

	// Support channels (China uses WeChat, Global uses WhatsApp)
	support: {
		useWeChat: isChinaApp,
		useWhatsApp: !isChinaApp,
		weChatUrl: "https://mp.weixin.qq.com/s/OtqDxSQd6nOEWlgLWVv6PA",
		weChatWorkDesktop: "https://work.weixin.qq.com/ca/cawcdedb60843e98ed",
		weChatWorkMobile: "https://work.weixin.qq.com/ca/cawcde94890348f81f",
		whatsAppUrl: "https://wa.me/447723497306",
	},

	// Google services (disabled for China)
	google: {
		isEnabled: !isChinaApp,
		recaptchaEnabled: !isChinaApp,
		analyticsEnabled: !isChinaApp,
	},
} as const;

export type Config = typeof config;

// Legacy alias for backwards compatibility
export const Config = config;
